"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Person } from "@/features/people/types";
import { ApiError } from "@/shared/api-client";
import { useSessionStatus } from "@/shared/hooks/use-session-status";
import { authKeys } from "@/shared/lib/query-keys";
import { authApi } from "./auth-api";

export const authQueries = {
  me: () =>
    queryOptions({
      queryKey: authKeys.me(),
      queryFn: ({ signal }) => authApi.me(signal),
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      retry: (count, err) => {
        // Never retry auth failures — apiFetch already handled refresh.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
        return count < 1;
      },
    }),
};

/** Canonical identity query. Fires only when the controller thinks we may be in. */
export function useMe() {
  const status = useSessionStatus();
  return useQuery({
    ...authQueries.me(),
    enabled: status === "authenticated" || status === "unknown",
  });
}

/** The viewer as every other person is drawn — the shape a sender, an
 *  owner or a shipper carries — so "is this me?" is one id comparison. */
export function useMePerson(): Person | null {
  const { data: me } = useMe();
  // One identity per /me payload: callers key callbacks on it.
  return useMemo(
    () =>
      me
        ? {
            user_id: me.id,
            display_name: me.display_name,
            email: me.email,
            avatar_url: me.avatar_url ?? null,
          }
        : null,
    [me],
  );
}

/** The sign-in providers this deployment has set up — a deploy-time fact,
 *  so it is fetched once per visit. */
export function useSignInProviders() {
  return useQuery({
    queryKey: authKeys.providers(),
    queryFn: ({ signal }) => authApi.signInProviders(signal),
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

/** Active device sessions (refresh families). */
export function useSessions() {
  const status = useSessionStatus();
  return useQuery({
    queryKey: authKeys.sessions(),
    queryFn: ({ signal }) => authApi.listSessions(signal),
    staleTime: 30_000,
    enabled: status === "authenticated",
  });
}

/** Registered passkeys. */
export function usePasskeys() {
  const status = useSessionStatus();
  return useQuery({
    queryKey: authKeys.passkeys(),
    queryFn: ({ signal }) => authApi.webauthnList(signal),
    staleTime: 30_000,
    enabled: status === "authenticated",
  });
}

/** Enrolled MFA methods + recovery-code count. */
export function useMfaStatus() {
  const status = useSessionStatus();
  return useQuery({
    queryKey: authKeys.mfa(),
    queryFn: ({ signal }) => authApi.mfaStatus(signal),
    staleTime: 30_000,
    enabled: status === "authenticated",
  });
}
